import { Image as RNImage } from 'react-native';
import { FormFieldData } from '../FormField.types.ts';
import {
  Icons,
  Image,
  SizeVariant,
  Text,
  VStack,
} from '@tonic/central-specialties-ui-themed';
import {
  Button,
  ButtonText,
  HStack,
  Icon,
  Pressable,
} from '@gluestack-ui/themed';
import { openImageFiles } from '../../../../../utils/openFileOrFiles.ts';
import { Schemas } from '@tonic/central-specialties-utils';
import { TextFormField } from './TextFormField.tsx';
import { fileToBase64 } from '../../../../../utils/fileToBase64.ts';

export interface ImagesFormFieldData {
  placeholder?: string;
  addButtonSize?: SizeVariant;
}

interface PartsImageData extends Partial<Schemas['ImageModel']> {
  isNew: boolean;
  id: string;
  url: string;
  thumbnailUrl: string;
  caption?: string;
  file?: File;
}

interface ImagesFormFieldExtended extends FormFieldData<'Images'> {
  displayCaption?: boolean;
  displayHorizontal?: boolean;
  handleImageDelete?: (id: string) => void;
}

// TODO: remove image button
export const ImagesFormField = ({
  value,
  onChange,
  placeholder,
  readonly,
  addButtonSize = 'lg',
  displayCaption = false,
  displayHorizontal = false,
  handleImageDelete,
}: ImagesFormFieldExtended) => {
  const Wrapper = displayHorizontal ? HStack : VStack;

  return (
    <Wrapper w="$full" gap="$2" justifyContent="flex-start">
      <HStack w="$full" flexWrap="wrap" gap="$4">
        {value && value.length ? (
          value.map((imageData: PartsImageData, i: number) => {
            return (
              <HStack
                w={displayHorizontal ? 'auto' : '$full'}
                key={`${imageData.id}-${i}`}
              >
                <Pressable
                  key={imageData.id}
                  position="relative"
                  onPress={() => {
                    // TODO: If form brought to mobile, this will need to be changed
                    window.open(imageData.url, '_blank');
                  }}
                >
                  {imageData?.url?.startsWith('data:') ? (
                    // Use RN Image for base64
                    <RNImage
                      source={{ uri: imageData.url }}
                      style={{ width: 100, height: 100 }}
                      resizeMode="cover"
                    />
                  ) : (
                    // Use Gluestack Image for regular URLs
                    <Image
                      source={{ uri: imageData.thumbnailUrl || imageData.url }}
                      alt={'image:' + i}
                      size="md"
                    />
                  )}

                  {!readonly && (
                    <Pressable
                      borderRadius="$full"
                      bg="$black"
                      position="absolute"
                      top={-4}
                      right={-4}
                      onPress={() => {
                        const flatValue = value ? value.flat() : [];
                        const newValue = [...flatValue];
                        const itemToBeDeleted = newValue[i];
                        newValue.splice(i, 1);
                        onChange(newValue);
                        handleImageDelete &&
                          handleImageDelete(itemToBeDeleted?.id);
                      }}
                    >
                      <Icon color="$white" as={Icons.XCircle} />
                    </Pressable>
                  )}
                </Pressable>
                {displayCaption && (
                  <VStack ml={'$2'} w="$3/4">
                    <TextFormField
                      value={imageData.caption || ''}
                      onChange={(text) => {
                        const updatedCaption = text.slice(0, 100);
                        const updatedImage = {
                          ...imageData,
                          caption: updatedCaption,
                        };
                        const flatValue = value ? value.flat() : [];
                        const updatedImages = [...flatValue];
                        updatedImages[i] = updatedImage;
                        onChange(updatedImages);
                      }}
                      multiline={false}
                      label={'Description'}
                      placeholder={'Add an optional description'}
                    />
                    {!readonly && (
                      <Text fontSize={'$sm'} color="$gray500" ml={'$1'}>
                        {(imageData.caption || '').length}/100 characters
                      </Text>
                    )}
                  </VStack>
                )}
              </HStack>
            );
          })
        ) : (
          <Text>{(!readonly && placeholder) || 'No images'}</Text>
        )}
      </HStack>
      <HStack>
        {!readonly && (
          <Button
            bg="transparent"
            minWidth="auto"
            sx={{
              ':disabled': {
                opacity: 0.5,
              },
            }}
            // Add button is disabled if there are already empties
            isDisabled={!value.every((v) => v)}
            onPress={async () => {
              // TODO: If form is brought to mobile, this will need to be changed
              const imageFiles: File[] = await openImageFiles(true);
              if (imageFiles) {
                const imageData: PartsImageData[] =
                  (await Promise.all(
                    imageFiles.map(async (imgFile) => {
                      try {
                        const base64Url = await fileToBase64(imgFile);

                        return {
                          isNew: true, // add this so we know which images are new and need to be uploaded upon update
                          id: `${imgFile.name} - ${Date.now()}`,
                          url: base64Url,
                          thumbnailUrl: base64Url,
                          file: imgFile,
                        };
                      } catch (error) {
                        console.error('Error processing image file: ', error);
                      }
                    }),
                  )) ?? [];
                const currentImages = value ? value.flat() : [];
                const newValue = [...currentImages, ...imageData];
                onChange(newValue);
              }
            }}
            px="$0"
          >
            <Icon
              size={addButtonSize}
              as={Icons.PlusCircle}
              color="$textNormal"
            />
            <ButtonText size={addButtonSize} color="$textNormal">
              Add Photo
            </ButtonText>
          </Button>
        )}
      </HStack>
    </Wrapper>
  );
};
